import { Link } from "react-router-dom";
import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";

// Content files bundled at build time via Vite's ?raw import.
// @ts-ignore
import termsRaw from "@/content/legal/terms.mdx?raw";
// @ts-ignore
import privacyRaw from "@/content/legal/privacy.mdx?raw";
// @ts-ignore
import refundsRaw from "@/content/legal/refunds.mdx?raw";

const CONTENT_MAP: Record<string, string> = {
  terms: termsRaw as string,
  privacy: privacyRaw as string,
  refunds: refundsRaw as string,
};

import { LocalDateTime } from "@/components/ui/local-datetime";
import type { LegalPage } from "@/lib/legal";
import { cn } from "@/lib/utils";

// Renders legal copy as Markdown (not full MDX) so angle brackets and other
// text in `content/legal/*.mdx` are not parsed as JSX - which was breaking
// pages when content included patterns like `<your-order-id>`.
//
// Internal paths use next/link; external http(s) links open in a new tab.

const linkClass =
  "text-brand-ink underline underline-offset-[3px] decoration-brand-ink/30 hover:decoration-brand-ink";

const markdownComponents: Components = {
  a({ href, children, className, ...rest }) {
    if (href?.startsWith("/")) {
      return (
        <Link to={href} className={cn(linkClass, className)} {...rest}>
          {children}
        </Link>
      );
    }
    return (
      <a
        href={href}
        className={cn(linkClass, className)}
        target="_blank"
        rel="noopener noreferrer"
        {...rest}
      >
        {children}
      </a>
    );
  },
  /** One title comes from the shell header; the file still starts with `# …` in git - strip it below, but map any remaining h1 to h2 for valid outline. */
  h1: ({ children, className, ...rest }) => (
    <h2
      className={cn("mt-10 text-[18px] font-semibold", className)}
      {...rest}
    >
      {children}
    </h2>
  ),
};

export function LegalPageView({ page }: { page: LegalPage }) {
  const raw = CONTENT_MAP[page.slug] ?? "";
  const source = raw.replace(/^\s*#[^\n]+\n+/, "").trimStart();

  return (
    <article>
      <header className="mb-8 border-b border-black/[0.06] pb-6">
        <p className="brand-eyebrow">Legal</p>
        <h1 className="brand-heading brand-heading-lg mt-2">{page.title}</h1>
        <p className="brand-body mt-3 text-[14px]">{page.description}</p>
        <p className="mt-4 text-[12px] uppercase tracking-[0.06em] text-brand-muted">
          Last updated{" "}
          <LocalDateTime
            iso={page.lastUpdated}
            preset="longDate"
            dateOnly
            as="time"
            className="font-medium"
          />
        </p>
      </header>

      <div
        className={[
          "prose prose-sm max-w-none text-[14px] leading-[1.7] text-brand-ink",
          "[&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-[18px] [&_h2]:font-semibold",
          "[&_h3]:mt-7 [&_h3]:mb-2 [&_h3]:text-[15px] [&_h3]:font-semibold",
          "[&_p]:my-3 [&_p]:text-brand-ink/85",
          "[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:text-brand-ink/85",
          "[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:text-brand-ink/85",
          "[&_li]:my-1",
          "[&_a]:text-brand-ink [&_a]:underline [&_a]:underline-offset-[3px] [&_a]:decoration-brand-ink/30 hover:[&_a]:decoration-brand-ink",
          "[&_code]:rounded [&_code]:bg-black/[0.06] [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[12.5px] [&_code]:font-mono",
          "[&_strong]:font-semibold [&_strong]:text-brand-ink",
        ].join(" ")}
      >
        <ReactMarkdown components={markdownComponents}>{source}</ReactMarkdown>
      </div>
    </article>
  );
}
